import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, Select, TimePicker, message } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  DdDetailsIcon,
  DdAssigneeIcon,
  DdRecurrenceIcon,
  DdClockIcon,
  PaperclipIcon,
  TrashIcon,
  TemplateDocIcon,
  ImagePlaceholderIcon,
} from '../../assets/icons';
import { useGetUsersQuery, userName } from '../../features/users/usersApi';
import { useUploadS3Mutation } from '../../features/uploads/uploadApi';
import {
  useGetDirectionQuery,
  useCreateDirectionMutation,
  useUpdateDirectionMutation,
  dirTitle,
  dirOneLine,
  dirDescription,
  dirPriority,
  dirDueDate,
  dirDueTime,
  dirDuration,
  dirFrequency,
  dirStartDate,
  dirEndDate,
  dirIsRecurring,
  dirMonthlyPattern,
  dirWeekday,
  type DailyDirection,
  type DirectionAsset,
} from '../../features/dailyDirections/dailyDirectionApi';
import {
  useGetAssigneesQuery,
  useGetAttachmentsQuery,
  assigneeName,
  attachmentName,
  type Assignee,
  type Attachment,
} from '../../features/activity/activityApi';

// Needed for parsing time-only strings ("HH:mm:ss") back into the TimePicker.
dayjs.extend(customParseFormat);

const FIELD =
  'w-full rounded-lg border border-transparent bg-[#f9fafb] px-4 text-sm text-ink placeholder:text-ink-secondary transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

const PRIORITIES: { key: string; lvl: string; label: string; critical?: boolean }[] = [
  { key: 'low', lvl: 'Level 01', label: 'Low' },
  { key: 'medium', lvl: 'Level 02', label: 'Medium' },
  { key: 'high', lvl: 'Level 03', label: 'High' },
  { key: 'critical', lvl: 'Level 04', label: 'Critical', critical: true },
];

interface DDFormValues {
  title: string;
  oneLine: string;
  description: string;
  assignees: number[];
  priority: string;
  dueDate: string;
  dueTime: string;
  duration: string;
  repeat: boolean;
  frequency: string;
  monthlyPattern: string;
  weekday: string;
  startDate: string;
  endDate: string;
  assets: DirectionAsset[];
}

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'on_status_changed', label: 'On Status Changed' },
];
const MONTHLY_PATTERNS = [
  'Same day each month',
  'First Saturday',
  'First day of the month',
  'Last day of the month',
];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const validationSchema = Yup.object({
  title: Yup.string().trim().required('Strategic title is required'),
  oneLine: Yup.string().trim().required('One-line direction is required'),
});

function Card({
  Icon,
  iconBg = 'bg-brand-shade text-brand',
  title,
  action,
  children,
}: {
  Icon: ReactNode;
  iconBg?: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-6 rounded-xl border border-[#f3f4f6] bg-white p-6 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={['flex size-8 shrink-0 items-center justify-center rounded-lg', iconBg].join(' ')}>
            {Icon}
          </span>
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Label({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-xs font-medium text-ink">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </span>
  );
}

/** The numeric user id of an assignee record (user may be an id or a nested object). */
const assigneeUserId = (a: Assignee): number | undefined =>
  typeof a.user === 'number' ? a.user : (a.user?.id ?? undefined);

/** Map an activity Attachment to the form's asset shape. */
const attachmentToAsset = (a: Attachment): DirectionAsset => ({
  id: a.id,
  file_url: a.file_url ?? undefined,
  file_type: a.file_type ?? undefined,
  name: attachmentName(a),
});

export default function CreateDailyDirectionPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  const { data: editDirection } = useGetDirectionQuery(editId!, { skip: !isEdit });
  // Edit mode: assignees & assets come from the activity endpoints (keyed by entity).
  const { data: editAssignees } = useGetAssigneesQuery(
    { entityType: 'DailyDirection', entityId: editId! },
    { skip: !isEdit },
  );
  const { data: editAttachments } = useGetAttachmentsQuery(
    { entityType: 'DailyDirection', entityId: editId! },
    { skip: !isEdit },
  );
  const [createDirection, { isLoading: isCreating }] = useCreateDirectionMutation();
  const [updateDirection, { isLoading: isUpdating }] = useUpdateDirectionMutation();
  const [uploadS3] = useUploadS3Mutation();
  const [uploading, setUploading] = useState(false);
  const saving = isCreating || isUpdating;

  // Assignee dropdown — active users, server-side search.
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setUserSearch(userSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [userSearchInput]);
  const { data: usersData } = useGetUsersQuery({
    isActive: true,
    all: true,
    ...(userSearch ? { search: userSearch } : {}),
  });
  // Accumulate id → name so selected assignees keep their labels when a search narrows the list.
  const [nameMap, setNameMap] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!usersData?.items) return;
    setNameMap((prev) => {
      const next = { ...prev };
      usersData.items.forEach((u) => {
        next[u.id] = userName(u);
      });
      return next;
    });
  }, [usersData]);

  const formik = useFormik<DDFormValues>({
    initialValues: {
      title: '',
      oneLine: '',
      description: '',
      assignees: [],
      priority: 'high',
      dueDate: '',
      dueTime: '',
      duration: '',
      repeat: false,
      frequency: 'daily',
      monthlyPattern: 'Same day each month',
      weekday: 'Monday',
      startDate: '',
      endDate: '',
      assets: [],
    },
    validationSchema,
    onSubmit: (values) => submit(values, undefined),
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;
  const errorOf = (name: keyof DDFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  const submit = async (values: DDFormValues, status?: string) => {
    const payload = {
      title: values.title.trim(),
      oneLine: values.oneLine.trim(),
      description: values.description.trim(),
      priority: values.priority,
      dueDate: values.dueDate || undefined,
      dueTime: values.dueTime || undefined,
      duration: values.duration || undefined,
      repeat: values.repeat,
      ...(values.repeat
        ? {
            frequency: values.frequency,
            // "Repeat Pattern" applies to monthly; "Repeats On" weekday applies to weekly.
            ...(values.frequency === 'monthly' ? { monthlyPattern: values.monthlyPattern } : {}),
            ...(values.frequency === 'weekly' ? { weekday: values.weekday } : {}),
            startDate: values.startDate || undefined,
            endDate: values.endDate || undefined,
          }
        : {}),
      assignees: values.assignees,
      assets: values.assets,
      ...(status ? { status } : {}),
    };
    try {
      if (isEdit && editId) {
        await updateDirection({ id: editId, patch: payload }).unwrap();
      } else {
        await createDirection(payload).unwrap();
      }
      navigate('/user/directions');
    } catch {
      message.error(`Could not ${isEdit ? 'update' : 'create'} the direction. Please try again.`);
    }
  };

  // Upload picked files to S3, then append the returned URLs to the assets list.
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: DirectionAsset[] = [];
      for (const file of Array.from(files)) {
        const res = await uploadS3(file).unwrap();
        uploaded.push({
          file_url: res.url,
          file_type: res.content_type || file.type,
          name: file.name,
        });
      }
      setFieldValue('assets', [...values.assets, ...uploaded]);
    } catch {
      message.error('File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  const removeAsset = (idx: number) =>
    setFieldValue('assets', values.assets.filter((_, i) => i !== idx));

  // Assignee options = fetched users, plus any selected id the current search dropped.
  const userOptions = useMemo(() => {
    const fetched = (usersData?.items ?? []).map((u) => ({ value: u.id, label: userName(u) }));
    const ids = new Set(fetched.map((o) => o.value));
    const extra = values.assignees
      .filter((id) => !ids.has(id))
      .map((id) => ({ value: id, label: nameMap[id] || `User ${id}` }));
    return [...fetched, ...extra];
  }, [usersData, values.assignees, nameMap]);

  // Edit mode: prefill from the loaded direction.
  useEffect(() => {
    if (!editDirection) return;
    const d = editDirection as DailyDirection;
    setFieldValue('title', dirTitle(d) === 'Untitled direction' ? '' : dirTitle(d));
    setFieldValue('oneLine', dirOneLine(d));
    setFieldValue('description', dirDescription(d));
    setFieldValue('priority', (dirPriority(d) || 'high').toLowerCase());
    setFieldValue('dueDate', dirDueDate(d));
    setFieldValue('dueTime', dirDueTime(d));
    setFieldValue('duration', dirDuration(d));
    setFieldValue('repeat', dirIsRecurring(d));
    if (dirFrequency(d)) setFieldValue('frequency', dirFrequency(d));
    if (dirMonthlyPattern(d)) setFieldValue('monthlyPattern', dirMonthlyPattern(d));
    if (dirWeekday(d)) setFieldValue('weekday', dirWeekday(d));
    setFieldValue('startDate', dirStartDate(d));
    setFieldValue('endDate', dirEndDate(d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDirection]);

  // Edit mode: prefill assignees from /activity/assignee/ (keep their names for the Select).
  useEffect(() => {
    if (!editAssignees) return;
    const ids = editAssignees
      .map(assigneeUserId)
      .filter((v): v is number => typeof v === 'number');
    setFieldValue('assignees', ids);
    setNameMap((prev) => {
      const next = { ...prev };
      editAssignees.forEach((a) => {
        const uid = assigneeUserId(a);
        if (uid != null) next[uid] = assigneeName(a);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAssignees]);

  // Edit mode: prefill assets from /activity/attachment/.
  useEffect(() => {
    if (!editAttachments) return;
    setFieldValue('assets', editAttachments.map(attachmentToAsset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAttachments]);

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <Breadcrumbs
          items={[
            { label: 'Daily Directions', to: '/user/directions' },
            { label: isEdit ? 'Edit Daily Direction' : 'Create Daily Direction' },
          ]}
        />
        <h1 className="text-2xl font-semibold text-ink">
          {isEdit ? 'Edit Daily Direction' : 'Create Daily Direction'}
        </h1>
        <p className="max-w-[576px] text-sm text-ink-secondary">
          Initialize a new operational directive. Define the scope, priority, and temporal
          constraints for tactical execution.
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Direction Details */}
          <Card Icon={<DdDetailsIcon className="size-4" />} title="Direction Details">
            <div className="flex flex-col gap-5">
              <label className="flex flex-col gap-2">
                <Label required>Strategic Title</Label>
                <input
                  name="title"
                  placeholder="e.g., Q4 Logistics Re-alignment"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={FIELD}
                />
                {errorOf('title') && (
                  <span className="text-xs font-medium text-[#ef4444]">{errorOf('title')}</span>
                )}
              </label>
              <label className="flex flex-col gap-2">
                <Label required>One-line Direction</Label>
                <input
                  name="oneLine"
                  placeholder="Summarize the core objective in under 100 characters"
                  value={values.oneLine}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={FIELD}
                />
                {errorOf('oneLine') && (
                  <span className="text-xs font-medium text-[#ef4444]">{errorOf('oneLine')}</span>
                )}
              </label>
              <label className="flex flex-col gap-2">
                <Label>Full Operational Description</Label>
                <textarea
                  name="description"
                  rows={4}
                  placeholder="Provide detailed context, secondary objectives, and mission-critical notes..."
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={[FIELD, 'resize-none py-3'].join(' ')}
                />
              </label>
            </div>
          </Card>

          {/* Assignee */}
          <Card Icon={<DdAssigneeIcon className="h-3.5 w-[18px]" />} title="Assignee">
            <div className="flex flex-col gap-2">
              <Label>Select Users</Label>
              <Select
                mode="multiple"
                className="field-muted w-full"
                placeholder="Choose assignees..."
                showSearch
                filterOption={false}
                onSearch={setUserSearchInput}
                value={values.assignees}
                onChange={(v: number[]) => setFieldValue('assignees', v)}
                options={userOptions}
              />
            </div>
          </Card>

          {/* Recurrence */}
          <Card
            Icon={<DdRecurrenceIcon className="size-3.5" />}
            title="Recurrence"
            action={
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-ink">Repeat this Direction</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={values.repeat}
                  onClick={() => setFieldValue('repeat', !values.repeat)}
                  className={['relative h-5 w-11 shrink-0 rounded-full transition-colors', values.repeat ? 'bg-brand' : 'bg-[#d1d5db]'].join(' ')}
                >
                  <span
                    className={[
                      'absolute top-0 size-5 rounded-full border-4 bg-white transition-all',
                      values.repeat ? 'left-6 border-brand' : 'left-0 border-[#46556f]',
                    ].join(' ')}
                  />
                </button>
              </div>
            }
          >
            <div
              className={[
                'flex flex-col gap-4',
                values.repeat ? '' : 'pointer-events-none opacity-50',
              ].join(' ')}
            >
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex flex-1 flex-col gap-2">
                  <Label>Frequency</Label>
                  <Select
                    className="field-muted w-full"
                    value={values.frequency}
                    onChange={(v) => setFieldValue('frequency', v)}
                    options={FREQUENCIES}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <Label>Start Date</Label>
                  <DatePicker
                    className="field-muted w-full"
                    format="MM/DD/YYYY"
                    placeholder="mm/dd/yyyy"
                    value={values.startDate ? dayjs(values.startDate) : null}
                    onChange={(d) => setFieldValue('startDate', d ? d.format('YYYY-MM-DD') : '')}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <Label>End Date</Label>
                  <DatePicker
                    className="field-muted w-full"
                    format="MM/DD/YYYY"
                    placeholder="mm/dd/yyyy"
                    value={values.endDate ? dayjs(values.endDate) : null}
                    onChange={(d) => setFieldValue('endDate', d ? d.format('YYYY-MM-DD') : '')}
                  />
                </div>
              </div>

              {/* Frequency-dependent options */}
              {values.frequency === 'monthly' && (
                <div className="flex flex-col gap-2 sm:max-w-[220px]">
                  <Label>Repeat Pattern</Label>
                  <Select
                    className="field-muted w-full"
                    value={values.monthlyPattern}
                    onChange={(v) => setFieldValue('monthlyPattern', v)}
                    options={MONTHLY_PATTERNS.map((p) => ({ value: p, label: p }))}
                  />
                </div>
              )}
              {values.frequency === 'weekly' && (
                <div className="flex flex-col gap-2 sm:max-w-[220px]">
                  <Label>Repeats On</Label>
                  <Select
                    className="field-muted w-full"
                    value={values.weekday}
                    onChange={(v) => setFieldValue('weekday', v)}
                    options={WEEKDAYS.map((d) => ({ value: d, label: d }))}
                  />
                </div>
              )}
              {values.frequency === 'on_status_changed' && (
                <p className="text-xs text-ink-secondary">
                  This direction re-triggers automatically whenever the linked status changes — no
                  fixed schedule needed.
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Priority */}
          <Card
            Icon={<span className="font-black text-ink">!</span>}
            iconBg="bg-[#f3f4f6] text-ink"
            title="Priority"
          >
            <div className="grid grid-cols-2 gap-3">
              {PRIORITIES.map((p) => {
                const selected = values.priority === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setFieldValue('priority', p.key)}
                    className={[
                      'flex flex-col gap-1 rounded-lg p-3 text-left transition-colors',
                      selected ? 'border-2 border-brand bg-brand-shade' : 'border border-transparent bg-[#f9fafb] hover:border-brand/30',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'text-[10px] font-semibold uppercase tracking-[0.25px]',
                        selected ? 'text-brand' : p.critical ? 'text-[#ef4444]' : 'text-ink-secondary',
                      ].join(' ')}
                    >
                      {p.lvl}
                    </span>
                    <span className="text-sm font-medium text-ink">{p.label}</span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Schedule */}
          <Card Icon={<DdClockIcon className="size-3.5" />} title="Schedule">
            <div className="flex flex-col gap-4">
              <label className="flex flex-col gap-2">
                <Label>Due Date</Label>
                <DatePicker
                  className="field-muted w-full"
                  format="MM/DD/YYYY"
                  placeholder="mm/dd/yyyy"
                  value={values.dueDate ? dayjs(values.dueDate) : null}
                  onChange={(d) => setFieldValue('dueDate', d ? d.format('YYYY-MM-DD') : '')}
                />
              </label>
              <div className="flex gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <Label>Due Time</Label>
                  <TimePicker
                    className="field-muted w-full"
                    use12Hours
                    format="hh:mm A"
                    placeholder="05:00 PM"
                    value={values.dueTime ? dayjs(values.dueTime, 'HH:mm:ss') : null}
                    onChange={(t) => setFieldValue('dueTime', t ? t.format('HH:mm:ss') : '')}
                  />
                </div>
                <label className="flex flex-1 flex-col gap-2">
                  <Label>Duration</Label>
                  <input
                    name="duration"
                    placeholder="e.g. 2 Hours"
                    value={values.duration}
                    onChange={handleChange}
                    className={FIELD}
                  />
                </label>
              </div>
            </div>
          </Card>

          {/* Assets */}
          <Card Icon={<PaperclipIcon className="h-3.5 w-3 text-brand" />} title="Assets">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] p-6 text-center transition-colors hover:border-brand/50">
                  <TemplateDocIcon className="h-6 w-[18px] text-ink-secondary" />
                  <span className="text-xs font-medium text-ink">Upload Documents</span>
                  <span className="text-[10px] text-ink-secondary">PDF, DOC up to 10MB</span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    multiple
                    className="hidden"
                    onChange={(e) => onFiles(e.target.files)}
                  />
                </label>
                <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] p-6 text-center transition-colors hover:border-brand/50">
                  <ImagePlaceholderIcon className="size-6 text-ink-secondary" />
                  <span className="text-xs font-medium text-ink">Upload Photos</span>
                  <span className="text-[10px] text-ink-secondary">JPG, PNG captures</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    multiple
                    className="hidden"
                    onChange={(e) => onFiles(e.target.files)}
                  />
                </label>
              </div>

              {uploading && <span className="text-xs text-ink-secondary">Uploading…</span>}

              {values.assets.length > 0 && (
                <div className="flex flex-col gap-2">
                  {values.assets.map((a, i) => (
                    <div
                      key={`${a.file_url}-${i}`}
                      className="flex items-center gap-2 rounded-lg bg-[#f9fafb] px-3 py-2"
                    >
                      <PaperclipIcon className="h-3.5 w-3 shrink-0 text-ink-secondary" />
                      <a
                        href={a.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 truncate text-xs text-brand hover:underline"
                      >
                        {a.name || a.file_url}
                      </a>
                      <button
                        type="button"
                        aria-label="Remove file"
                        onClick={() => removeAsset(i)}
                        className="shrink-0 text-ink-secondary transition-colors hover:text-red-500"
                      >
                        <TrashIcon className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-4 border-t border-[#e5e7eb] pt-6">
        <button
          type="button"
          onClick={() => submit(values, 'Draft')}
          disabled={saving}
          className="rounded-lg px-6 py-2.5 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade disabled:opacity-60"
        >
          Save Draft
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-brand px-6 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-60"
        >
          {saving ? 'Saving…' : isEdit ? 'Update' : 'Submit'}
        </button>
      </div>
    </form>
  );
}
